import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { usePageMeta } from '../hooks/usePageMeta';

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: '404 — My Bracket Sim',
    description: 'Page not found.',
    path: '/404',
  });

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 overflow-hidden relative">
      {/* Busted bracket SVG — lines breaking apart */}
      <svg
        viewBox="0 0 400 300"
        className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none"
        preserveAspectRatio="xMidYMid slice"
      >
        {/* Left side — intact but fading */}
        <g stroke="currentColor" strokeWidth="2" fill="none" className="text-foreground">
          <line x1="20" y1="30" x2="60" y2="30" />
          <line x1="20" y1="60" x2="60" y2="60" />
          <line x1="60" y1="30" x2="60" y2="60" />
          <line x1="60" y1="45" x2="100" y2="45" />

          <line x1="20" y1="90" x2="60" y2="90" />
          <line x1="20" y1="120" x2="60" y2="120" />
          <line x1="60" y1="90" x2="60" y2="120" />
          <line x1="60" y1="105" x2="100" y2="105" />

          <line x1="100" y1="45" x2="100" y2="105" />
          <line x1="100" y1="75" x2="140" y2="75" />
        </g>

        {/* Right side — broken, scattered */}
        <g stroke="currentColor" strokeWidth="2" fill="none" className="text-foreground" opacity="0.5">
          <line x1="380" y1="30" x2="345" y2="35" />
          <line x1="380" y1="60" x2="350" y2="55" />
          <line x1="340" y1="28" x2="338" y2="65" />

          <line x1="385" y1="150" x2="348" y2="155" />
          <line x1="378" y1="185" x2="342" y2="178" />

          <line x1="300" y1="42" x2="302" y2="110" />
          <line x1="298" y1="75" x2="265" y2="80" />
        </g>

        {/* Championship line — snapped */}
        <g stroke="hsl(17, 81%, 54%)" strokeWidth="3" fill="none" opacity="0.3">
          <line x1="155" y1="135" x2="185" y2="135" />
          {/* Gap where the connection should be */}
          <line x1="215" y1="138" x2="245" y2="132" />
        </g>

        {/* Scattered fragments */}
        <g stroke="currentColor" strokeWidth="1.5" fill="none" className="text-foreground" opacity="0.3">
          <line x1="260" y1="250" x2="280" y2="255" />
          <line x1="310" y1="260" x2="325" y2="265" />
          <line x1="50" y1="260" x2="65" y2="270" />
          <line x1="150" y1="240" x2="170" y2="235" />
        </g>
      </svg>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-md">
        {/* 404 styled as a seed number */}
        <div className="flex items-center gap-1 mb-6">
          <span className="text-muted-foreground text-lg font-mono">(</span>
          <span className="font-mono text-8xl sm:text-9xl font-bold tracking-tighter text-primary">
            404
          </span>
          <span className="text-muted-foreground text-lg font-mono">)</span>
        </div>

        {/* Bracket-themed messaging */}
        <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-3">
          Bracket Busted
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8">
          This page got eliminated in the first round.
          <br />
          <span className="text-sm">No paths to victory here.</span>
        </p>

        {/* CTA */}
        <Button
          size="lg"
          onClick={() => navigate('/')}
          className="px-8"
        >
          Back to the Tournament
        </Button>
      </div>
    </div>
  );
};

export default NotFound;
